import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import Modal from '@/common/components/Modal'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import SelectController from '@/common/components/SelectController'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useUpdateContractBillDetails } from '@/features/privateCompanies/hooks/contract/useUpdateContractBillDetails'
import {
  UpdateContractBillDetailsSchema,
  updateContractBillDetailsSchema,
} from '@/features/privateCompanies/schemas/contract/updateBillDetails.schema'
import { CompanyContractType } from '@/features/privateCompanies/types/companyContract.type'
import { options as billGroupingModeOptions } from '@/features/privateCompanyLeadsFunnel/constants/billGroupingModeOptions.constant'
import { options as billSubGroupingModeOptions } from '@/features/privateCompanyLeadsFunnel/constants/billSubGroupingModeOptions.constant'
import { options as cycleOptions } from '@/features/privateCompanyLeadsFunnel/constants/cycleOptions.constant'
import { options as defaultContractCnpjOptions } from '@/features/privateCompanyLeadsFunnel/constants/defaultContractCnpjOptions.constant'
import { options as monthDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/monthDaysOptions.constant'
import { options as paymentMethodOptions } from '@/features/privateCompanyLeadsFunnel/constants/paymentMethodOptions.constant'
import { options as weekDaysOptions } from '@/features/privateCompanyLeadsFunnel/constants/weekDaysOptions.constants'
import { options as billByOptions } from "@/features/privateCompanyLeadsFunnel/constants/billByOptions.constant"
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface UpdateBillDetailsModalProps {
  contract?: CompanyContractType
  companyKind?: string
}

export const UpdateBillDetailsModal = ({ contract, companyKind }: UpdateBillDetailsModalProps) => {
  const queryClient = useQueryClient()

  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const getCycleStartDayDefaultValue = useMemo(() => {
    return contract?.cycle === 'weekly'
      ? contract?.cycle_start_day !== null && contract?.cycle_start_day !== undefined
        ? weekDaysOptions[contract?.cycle_start_day]
        : undefined
      : monthDaysOptions.find(({ value }) => value === `${contract?.cycle_start_day}`)
  }, [contract?.cycle])

  const {
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<UpdateContractBillDetailsSchema>({
    resolver: zodResolver(updateContractBillDetailsSchema),
  })

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['clientContract', contract?.id] })
    setIsOpen(false)
  }

  const { mutate: updateContractBillDetails, isPending: updateContractBillDetailsIsPending } =
    useUpdateContractBillDetails({
      contractId: contract?.id,
      onSuccess,
      onError: FormTransformers.errorsTransformer<UpdateContractBillDetailsSchema>({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<UpdateContractBillDetailsSchema> = (data) => {
    updateContractBillDetails(data)
  }

  const watchCycle = watch('contract.cycle')

  useEffect(() => {
    if (isOpen) {
      reset({
        contract: {
          cycle: cycleOptions.find(({ value }) => value === contract?.cycle),
          cycle_start_day: getCycleStartDayDefaultValue,
          closing_to_due_in_days: contract?.closing_to_due_in_days
            ? {
              label: `D+${contract?.closing_to_due_in_days}`,
              value: `${contract?.closing_to_due_in_days}`,
            }
            : undefined,
          bill_grouping_mode: billGroupingModeOptions.find(({ value }) => value === contract?.bill_grouping_mode),
          bill_sub_grouping_mode: billSubGroupingModeOptions.find(({ value }) => value === contract?.bill_sub_grouping_mode),
          bill_by: billByOptions.find(({ value }) => value === contract?.bill_by),
          payment_method: paymentMethodOptions.find(({ value }) => value === contract?.payment_method),
          due_to_suspension_in_days: contract?.due_to_suspension_in_days
            ? {
              label: `D+${contract?.due_to_suspension_in_days}`,
              value: `${contract?.due_to_suspension_in_days}`,
            }
            : undefined,
          cnpj_for_fiscal_document: defaultContractCnpjOptions.find(
            (option) => option.value === contract?.cnpj_for_fiscal_document,
          ),
        },
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (contract?.cycle !== 'fixed' && watchCycle?.value && watchCycle?.value === 'fixed') {
      setValue('contract.cycle_fixed_interval', undefined)
    }
  }, [watchCycle])

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onConfirm={handleSubmit(onSubmit)}
      Trigger={
        <Button
          name="open_update_bill_details_modal"
          operationKlassToCheckPermission={'Admin::Client::Contracts::UpdateContractBillInformation'}
          className="h-[2.125rem] text-sm text-white-800"
        >
          Editar
        </Button>
      }
      title="Editando detalhes da fatura"
      dataTestid="update_bill_details"
      bodyClassName="max-h-[calc(100vh-10rem)] overflow-y-auto"
      useCancellableFooter
      confirmButtonClassName="bg-blue-700"
      disabled={updateContractBillDetailsIsPending}
      confirmButtonIsLoading={updateContractBillDetailsIsPending}
      body={
        <>
          <form className="flex flex-col gap-y-4 text-white-800">
            <SelectController
              control={control}
              name="contract.cycle"
              label="Ciclo de fatura"
              options={cycleOptions}
              triggerOnChange={({ value }) => {
                setValue('contract.cycle_start_day', value === 'weekly' ? weekDaysOptions[0] : monthDaysOptions[0])
              }}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.cycle?.message}
            />

            <div className="flex gap-x-2">
              {watchCycle?.value === 'fixed' && (
                <MaskedTextField
                  label="Intervalo de ciclo fixo"
                  placeholder="Quantidade em dias"
                  mask='0000'
                  className="w-full"
                  {...register('contract.cycle_fixed_interval')}
                  errorMessage={errors?.contract?.cycle_fixed_interval?.message || undefined}
                  defaultValue={contract?.cycle_fixed_interval ?? ''}
                />
              )}
              {watchCycle?.value !== 'fixed' && (
                <SelectController
                  control={control}
                  name="contract.cycle_start_day"
                  label="Dia de abertura"
                  className="w-full"
                  options={watchCycle?.value === 'weekly' ? weekDaysOptions : monthDaysOptions(28)}
                  isDisabled={updateContractBillDetailsIsPending}
                  errorMessage={errors?.contract?.cycle_start_day?.message}
                />
              )}
              <SelectController
                control={control}
                name="contract.closing_to_due_in_days"
                label="Vencimento da fatura"
                className="w-full"
                options={monthDaysOptions(90).map((day) => ({ label: `D+${day.label}`, value: day.value }))}
                isDisabled={updateContractBillDetailsIsPending}
                errorMessage={errors?.contract?.closing_to_due_in_days?.message}
              />
            </div>

            <SelectController
              control={control}
              name="contract.bill_grouping_mode"
              label="Modo de agrupamento da fatura"
              options={billGroupingModeOptions.filter((option) => !option.company_kind || option.company_kind === companyKind)}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.bill_grouping_mode?.message}
            />

            <SelectController
              control={control}
              name="contract.bill_sub_grouping_mode"
              label="Modo de subagrupamento da fatura"
              options={billSubGroupingModeOptions}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.bill_sub_grouping_mode?.message}
            />

            <SelectController
              control={control}
              name="contract.bill_by"
              label="Segmentação da Fatura"
              errorMessage={errors?.contract?.bill_by?.message || undefined}
              defaultValue={billByOptions.find(({ value }) => value === contract?.bill_by)}
              options={billByOptions}
              isDisabled={updateContractBillDetailsIsPending}
            />

            <SelectController
              control={control}
              name="contract.payment_method"
              label="Método de pagamento"
              options={paymentMethodOptions}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.payment_method?.message}
            />

            <SelectController
              control={control}
              name="contract.due_to_suspension_in_days"
              label="Dias após vencimento até inadimplência"
              options={monthDaysOptions(90).map((day) => ({ label: `D+${day.label}`, value: day.value }))}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.due_to_suspension_in_days?.message}
            />

            <SelectController
              control={control}
              name="contract.cnpj_for_fiscal_document"
              label="CNPJ/CPF para emissão de NF"
              options={defaultContractCnpjOptions}
              isDisabled={updateContractBillDetailsIsPending}
              errorMessage={errors?.contract?.cnpj_for_fiscal_document?.message}
            />

            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[0.625rem] leading-tight font-light italic">
                Estas mudanças so podem ser feitas dentro de uma janela de manutenção, das 8 da manha ate as 8 da noite
              </p>
            </div>

            <div className="flex items-center gap-x-2">
              <InfoIcon strokeWidth={1} className="size-6 text-warning" />

              <p className="text-[0.625rem] leading-tight font-light italic">
                Estas mudanças so terão efeito após o fechamento da próxima fatura.
              </p>
            </div>
          </form>

          {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
        </>
      }
    />
  )
}
